import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// ============================================================================
// note-client-mod -- NotePanel
//
// 笔记管理面板容器（Slot 内容组件）。
// 集成：笔记本侧边栏 + 搜索工具栏 + 笔记列表 + 编辑器弹窗 + 笔记本管理弹窗。
// 响应式布局：移动端自动堆叠，桌面端左右分栏。
// ============================================================================
import * as React from 'react';
import { useTheme } from '../styles/use-theme.js';
import { useResponsive } from '../styles/use-responsive.js';
import { radius, spacing, typography, transition } from '../styles/theme.js';
import { KEYFRAMES_CSS } from '../styles/animations.js';
import { useNotes } from '../hooks/use-notes.js';
import { useNotebooks } from '../hooks/use-notebooks.js';
import { useNoteToggle } from '../hooks/use-note-toggle.js';
import { useDeleteNote } from '../hooks/use-delete-note.js';
import { useNoteStats } from '../hooks/use-note-stats.js';
import { NoteSidebar } from './note-sidebar.js';
import { NoteListItem } from './note-list-item.js';
import { NoteEditor } from './note-editor.js';
import { NotebookManager } from './notebook-manager.js';
import { PlusIcon, EmptyIcon, SearchIcon } from './icons.js';
const SORT_OPTIONS = [
    { id: '', label: '最近更新' },
    { id: 'createdAt', label: '创建时间' },
    { id: 'title', label: '标题' },
    { id: 'pinned', label: '置顶优先' },
];
export function NotePanel() {
    const { theme } = useTheme();
    const { isMobile } = useResponsive();
    // 列表查询（外部状态通过 useNotes 的 setter 更新）
    const { notes, pagination, loading, error, refresh, setPage, setNotebookId, setKeyword, setTag, setArchived, setFavorite, setSortBy, setSortOrder } = useNotes({ pageSize: 10 });
    const { notebooks, refresh: refreshNotebooks } = useNotebooks();
    const { stats, refresh: refreshStats } = useNoteStats();
    const { toggling, toggle } = useNoteToggle();
    const { deleting, deleteNote } = useDeleteNote();
    const [selectedEntry, setSelectedEntry] = React.useState('all');
    const [selectedNotebookId, setSelectedNotebookId] = React.useState(undefined);
    const [searchText, setSearchText] = React.useState('');
    const [tagFilter, setTagFilter] = React.useState('');
    const [sortBy, setSortByState] = React.useState('');
    const [editorOpen, setEditorOpen] = React.useState(false);
    const [editingNote, setEditingNote] = React.useState(null);
    const [managerOpen, setManagerOpen] = React.useState(false);
    const [deleteConfirm, setDeleteConfirm] = React.useState(null);
    const [sortOrderState, setSortOrderState] = React.useState('desc');
    // 注入全局动画样式
    React.useEffect(() => {
        const styleId = 'note-panel-keyframes';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.textContent = KEYFRAMES_CSS;
            document.head.appendChild(style);
        }
    }, []);
    // 入口切换 → 同步过滤参数
    const handleSelectEntry = (entry, notebookId) => {
        setSelectedEntry(entry);
        setSelectedNotebookId(notebookId);
        setArchived(entry === 'archived' ? true : undefined);
        setFavorite(entry === 'favorites' ? true : undefined);
        setNotebookId(entry === 'notebook' ? notebookId : undefined);
        setPage(1);
    };
    // 搜索（防抖 300ms）
    React.useEffect(() => {
        const timer = setTimeout(() => {
            setKeyword(searchText.trim() || undefined);
            setPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchText, setKeyword, setPage]);
    const handleTagChange = (value) => {
        setTagFilter(value);
        setTag(value || undefined);
        setPage(1);
    };
    const handleSortChange = (value) => {
        setSortByState(value);
        setSortBy(value || undefined);
    };
    const handleToggleSortOrder = () => {
        setSortOrderState((prev) => {
            const next = prev === 'asc' ? 'desc' : 'asc';
            setSortOrder(next);
            return next;
        });
    };
    const handleCreate = () => {
        setEditingNote(null);
        setEditorOpen(true);
    };
    const handleEdit = (note) => {
        setEditingNote(note);
        setEditorOpen(true);
    };
    const handleEditorClose = () => {
        setEditorOpen(false);
        setEditingNote(null);
    };
    const handleEditorSuccess = () => {
        setEditorOpen(false);
        setEditingNote(null);
        refresh();
        refreshNotebooks();
        refreshStats();
    };
    const handleToggleFavorite = async (note) => {
        const updated = await toggle(note._id, 'favorite');
        if (updated)
            refresh();
    };
    const handleDeleteConfirm = async () => {
        if (!deleteConfirm)
            return;
        const ok = await deleteNote(deleteConfirm._id);
        if (ok) {
            setDeleteConfirm(null);
            refresh();
            refreshStats();
        }
    };
    // 笔记本名称映射
    const notebookNames = React.useMemo(() => {
        const map = {};
        notebooks.forEach(nb => { map[nb._id] = nb.name; });
        return map;
    }, [notebooks]);
    const tagOptions = React.useMemo(() => {
        const items = (stats?.tags ?? []).map(t => ({ tag: t.tag, count: t.count }));
        return [{ tag: '', count: 0 }, ...items];
    }, [stats]);
    /* -- 工具栏 -- */
    const toolbar = (_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.lg, flexWrap: 'wrap' }, children: [_jsxs("div", { style: { position: 'relative', flex: '1 1 160px', minWidth: '140px' }, children: [_jsx("span", { style: { position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', display: 'inline-flex', color: theme.text.tertiary }, children: _jsx(SearchIcon, {}) }), _jsx("input", { placeholder: "\u641C\u7D22\u6807\u9898\u6216\u5185\u5BB9...", value: searchText, onChange: (e) => setSearchText(e.target.value), style: {
                            width: '100%',
                            padding: '7px 10px 7px 30px',
                            border: `1px solid ${theme.border.default}`,
                            borderRadius: radius.md,
                            fontSize: typography.fontSize.base,
                            outline: 'none',
                            backgroundColor: theme.bg.secondary,
                            color: theme.text.primary,
                            transition: `border-color ${transition.fast}, box-shadow ${transition.fast}`,
                        } })] }), _jsx("select", { value: tagFilter, onChange: (e) => handleTagChange(e.target.value), style: {
                    padding: '7px 10px',
                    border: `1px solid ${theme.border.default}`,
                    borderRadius: radius.md,
                    fontSize: typography.fontSize.base,
                    outline: 'none',
                    backgroundColor: theme.bg.secondary,
                    color: theme.text.primary,
                    cursor: 'pointer',
                }, children: tagOptions.map(option => (_jsx("option", { value: option.tag, children: option.tag ? `#${option.tag} (${option.count})` : '全部标签' }, option.tag || 'all'))) }), _jsx("select", { value: sortBy, onChange: (e) => handleSortChange(e.target.value), style: {
                    padding: '7px 10px',
                    border: `1px solid ${theme.border.default}`,
                    borderRadius: radius.md,
                    fontSize: typography.fontSize.base,
                    outline: 'none',
                    backgroundColor: theme.bg.secondary,
                    color: theme.text.primary,
                    cursor: 'pointer',
                }, children: SORT_OPTIONS.map(option => (_jsx("option", { value: option.id, children: option.label }, option.id || 'recent'))) }), _jsx("button", { onClick: handleToggleSortOrder, style: {
                    padding: '7px 10px',
                    border: `1px solid ${theme.border.default}`,
                    borderRadius: radius.md,
                    backgroundColor: theme.bg.secondary,
                    fontSize: typography.fontSize.base,
                    cursor: 'pointer',
                    color: theme.text.secondary,
                }, title: "\u5207\u6362\u5347\u964D\u5E8F", children: sortOrderState === 'desc' ? '↓' : '↑' }), _jsxs("button", { onClick: handleCreate, style: {
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: spacing.xs,
                    marginLeft: 'auto',
                    padding: '7px 14px',
                    backgroundColor: theme.status.primary,
                    color: theme.text.inverse,
                    border: 'none',
                    borderRadius: radius.md,
                    fontSize: typography.fontSize.base,
                    fontWeight: typography.fontWeight.medium,
                    cursor: 'pointer',
                    boxShadow: theme.shadow.sm,
                }, children: [_jsx(PlusIcon, {}), _jsx("span", { children: "\u65B0\u5EFA\u7B14\u8BB0" })] })] }));
    /* -- 内容区 -- */
    let contentElement;
    if (loading && notes.length === 0) {
        contentElement = (_jsx("div", { style: { padding: '40px', textAlign: 'center', color: theme.text.tertiary, fontSize: typography.fontSize.base }, children: _jsx("span", { style: { animation: 'note-pulse 1.5s ease-in-out infinite', display: 'inline-block' }, children: "\u52A0\u8F7D\u4E2D..." }) }));
    }
    else if (error) {
        contentElement = (_jsx("div", { style: { padding: spacing.lg, backgroundColor: theme.status.dangerBg, border: `1px solid ${theme.status.danger}40`, borderRadius: radius.md, color: theme.status.danger, fontSize: typography.fontSize.base }, children: error }));
    }
    else if (notes.length === 0) {
        contentElement = (_jsxs("div", { style: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', gap: spacing.sm }, children: [_jsx("span", { style: { color: theme.text.tertiary }, children: _jsx(EmptyIcon, {}) }), _jsx("p", { style: { margin: 0, fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.medium, color: theme.text.secondary }, children: "\u6682\u65E0\u7B14\u8BB0" }), _jsx("p", { style: { margin: 0, fontSize: typography.fontSize.sm, color: theme.text.tertiary }, children: "\u70B9\u51FB\u300C\u65B0\u5EFA\u7B14\u8BB0\u300D\u8BB0\u5F55\u7B2C\u4E00\u6761\u60F3\u6CD5" })] }));
    }
    else {
        contentElement = (_jsx("div", { style: { display: 'flex', flexDirection: 'column', gap: spacing.sm }, children: notes.map(note => (_jsx(NoteListItem, { note: note, notebookName: notebookNames[note.notebookId], onOpen: handleEdit, onToggleFavorite: handleToggleFavorite }, note._id))) }));
    }
    /* -- 分页 -- */
    let paginationElement = null;
    if (!loading && pagination && pagination.totalPages > 1) {
        const pageBtn = {
            padding: '6px 12px',
            border: `1px solid ${theme.border.default}`,
            borderRadius: radius.md,
            backgroundColor: theme.bg.secondary,
            fontSize: typography.fontSize.sm,
            cursor: 'pointer',
            color: theme.text.primary,
        };
        paginationElement = (_jsxs("div", { style: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: spacing.md, marginTop: spacing.xl }, children: [_jsx("button", { onClick: () => setPage(Math.max(1, pagination.page - 1)), disabled: pagination.page <= 1, style: pageBtn, children: "\u4E0A\u4E00\u9875" }), _jsxs("span", { style: { fontSize: typography.fontSize.sm, color: theme.text.tertiary }, children: [pagination.page, " / ", pagination.totalPages] }), _jsx("button", { onClick: () => setPage(Math.min(pagination.totalPages, pagination.page + 1)), disabled: pagination.page >= pagination.totalPages, style: pageBtn, children: "\u4E0B\u4E00\u9875" })] }));
    }
    /* -- 侧边栏 -- */
    const sidebar = (_jsx(NoteSidebar, { notebooks: notebooks, selected: selectedEntry, selectedNotebookId: selectedNotebookId, onSelect: handleSelectEntry, onManageNotebooks: () => setManagerOpen(true), compact: isMobile }));
    /* -- 弹窗 -- */
    const editorModal = editorOpen ? (_jsx(NoteEditor, { note: editingNote ?? undefined, notebooks: notebooks, defaultNotebookId: selectedEntry === 'notebook' ? selectedNotebookId : undefined, onSuccess: handleEditorSuccess, onCancel: handleEditorClose })) : null;
    const managerModal = managerOpen ? (_jsx(NotebookManager, { notebooks: notebooks, onClose: () => setManagerOpen(false), onChanged: () => { refreshNotebooks(); refresh(); } })) : null;
    let deleteModal = null;
    if (deleteConfirm) {
        deleteModal = (_jsx("div", { style: { position: 'fixed', inset: 0, backgroundColor: theme.bg.overlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }, onClick: () => setDeleteConfirm(null), children: _jsxs("div", { style: { backgroundColor: theme.bg.elevated, borderRadius: radius.xl, padding: spacing.xl, width: '360px', maxWidth: '90vw', boxShadow: theme.shadow.xl, animation: 'note-scale-in 0.2s ease forwards' }, onClick: (e) => e.stopPropagation(), children: [_jsx("h3", { style: { margin: '0 0 12px 0', fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.semibold, color: theme.text.primary }, children: "\u786E\u8BA4\u5220\u9664" }), _jsxs("p", { style: { margin: '0 0 20px 0', fontSize: typography.fontSize.base, color: theme.text.secondary, lineHeight: typography.lineHeight.relaxed }, children: ["\u786E\u5B9A\u8981\u5220\u9664\u7B14\u8BB0\u300C", deleteConfirm.title || '无标题', "\u300D\u5417\uFF1F\u5220\u9664\u540E\u65E0\u6CD5\u6062\u590D\u3002"] }), _jsxs("div", { style: { display: 'flex', justifyContent: 'flex-end', gap: spacing.sm }, children: [_jsx("button", { onClick: () => setDeleteConfirm(null), disabled: deleting, style: { padding: '8px 16px', border: `1px solid ${theme.border.default}`, borderRadius: radius.md, backgroundColor: theme.bg.secondary, fontSize: typography.fontSize.base, cursor: 'pointer', color: theme.text.secondary }, children: "\u53D6\u6D88" }), _jsx("button", { onClick: handleDeleteConfirm, disabled: deleting, style: { padding: '8px 16px', border: 'none', borderRadius: radius.md, backgroundColor: theme.status.danger, fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium, color: '#fff', cursor: 'pointer', opacity: deleting ? 0.6 : 1 }, children: deleting ? '删除中...' : '确认删除' })] })] }) }));
    }
    /* -- 主区域 -- */
    const main = (_jsxs("div", { style: { flex: 1, paddingLeft: isMobile ? 0 : '20px', minWidth: 0 }, children: [toolbar, contentElement, paginationElement] }));
    return (_jsxs("div", { style: { display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 0, maxWidth: '1024px', margin: '0 auto', padding: isMobile ? spacing.lg : spacing.xl, fontFamily: typography.fontFamily, minHeight: '500px' }, children: [_jsx("div", { style: isMobile ? { marginBottom: spacing.md } : { width: '200px', flexShrink: 0, borderRight: `1px solid ${theme.border.divider}`, paddingRight: spacing.lg }, children: sidebar }), main, editorModal, managerModal, deleteModal] }));
}
//# sourceMappingURL=note-panel.js.map